import { useCallback, useEffect, useRef, useState } from "react";
import type {
  PosterDoc,
  PosterImportCandidate,
  PosterSaveResponse,
  PosterShape,
  PosterSlot,
  PosterStateResponse,
  PosterStyleKey,
} from "@studio/shared";
import { ApiError, apiRequest } from "../lib/api";
import { loadSession } from "../lib/session";

/** 포스터 수업 학생 API — 서버 정본 = server/src/routes/poster.ts */

export function getPosterState(workId: string): Promise<PosterStateResponse> {
  return apiRequest<PosterStateResponse>(`/api/works/${encodeURIComponent(workId)}/poster`);
}

export function requestSlotImage(
  workId: string,
  slot: PosterSlot,
  input: { text: string; style?: PosterStyleKey | undefined; shape: PosterShape },
): Promise<{ job_id: string; cut_id: string }> {
  return apiRequest(`/api/works/${encodeURIComponent(workId)}/poster/cuts/${slot}/image`, {
    method: "POST",
    headers: { "Idempotency-Key": crypto.randomUUID() },
    body: JSON.stringify(input.style ? { text: input.text, style: input.style, shape: input.shape } : { text: input.text, shape: input.shape }),
  });
}

export function savePosterDoc(workId: string, doc: PosterDoc, baseRevision: number): Promise<PosterSaveResponse> {
  return apiRequest(`/api/works/${encodeURIComponent(workId)}/poster`, {
    method: "PUT",
    body: JSON.stringify({ doc, base_revision: baseRevision }),
  });
}

export function uploadPosterPhoto(workId: string, file: File): Promise<{ asset_id: string; url: string }> {
  const form = new FormData();
  form.set("photo", file);
  return apiRequest(`/api/works/${encodeURIComponent(workId)}/poster/photos`, { method: "POST", body: form });
}

export function listPosterImports(workId: string): Promise<{ items: PosterImportCandidate[] }> {
  return apiRequest(`/api/works/${encodeURIComponent(workId)}/poster/imports`);
}

export function importPicture(workId: string, sourceWorkId: string, slot: PosterSlot): Promise<{ cut_id: string; asset_id: string }> {
  return apiRequest(`/api/works/${encodeURIComponent(workId)}/poster/imports`, {
    method: "POST",
    body: JSON.stringify({ source_work_id: sourceWorkId, slot }),
  });
}

/**
 * 동일 출처 중계(D16)로 그림 바이트를 받는다 — PDF·PNG·마커처럼 «읽어야» 하는 곳 전용.
 * 🔑 화면 표시는 서명 주소(`image_url`)로 한다. 중계는 우리 서버를 거치므로 꼭 필요할 때만.
 */
export async function fetchPosterMedia(workId: string, assetId: string): Promise<Blob> {
  return (await fetchPosterMediaWithPrint(workId, assetId, false)).blob;
}

/**
 * `print` = A2 인쇄용 사본(서버 업스케일 · server/src/lib/poster-print.ts)을 달라는 요청.
 * 사본이 아직 없으면 서버가 원본을 주고 만들기 시작한다 — `printReady` 가 false.
 */
export async function fetchPosterMediaWithPrint(workId: string, assetId: string, print: boolean): Promise<{ blob: Blob; printReady: boolean }> {
  const token = loadSession(workId)?.token;
  const response = await fetch(`/api/works/${encodeURIComponent(workId)}/poster/media/${encodeURIComponent(assetId)}${print ? "?print=1" : ""}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new ApiError(response.status, "MEDIA_FAILED", "그림을 불러오지 못했어요.");
  return { blob: await response.blob(), printReady: response.headers.get("x-poster-print") !== "pending" };
}

export interface PosterQuery {
  data: PosterStateResponse | null;
  loading: boolean;
  error: ApiError | null;
  refresh: () => Promise<PosterStateResponse | null>;
}

/**
 * 포스터 상태. 그림을 만드는 칸이 있으면 3초마다 다시 읽는다 — 완료되면 그림 주소가 붙어서 온다.
 * 🔑 칸별 진행은 잡 폴링(useJobProgress)이 아니라 이 한 요청으로 본다(칸 셋 = 요청 하나).
 */
export function usePoster(workId: string): PosterQuery {
  const [data, setData] = useState<PosterStateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const alive = useRef(true);
  const refresh = useCallback(async (): Promise<PosterStateResponse | null> => {
    try {
      const next = await getPosterState(workId);
      if (alive.current) {
        setData(next);
        setError(null);
      }
      return next;
    } catch (caught) {
      if (alive.current) {
        setError(caught instanceof ApiError ? caught : new ApiError(0, "NETWORK", "포스터를 불러오지 못했어요."));
      }
      return null;
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    alive.current = true;
    void refresh();
    return () => {
      alive.current = false;
    };
  }, [refresh]);

  const generating = Boolean(data?.cuts.some((cut) => cut.active_job));
  useEffect(() => {
    if (!generating) return undefined;
    const timer = window.setInterval(() => void refresh(), 3_000);
    return () => window.clearInterval(timer);
  }, [generating, refresh]);

  return { data, loading, error, refresh };
}
